
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  getTagTypes,
  addTagType,
  addTag,
} from '@pingum/app-state-management/tagType/actions';
import {
  getTags,
  updateTagList,
  editTag,
} from '@pingum/app-state-management/tag/actions';
import { addError } from '@pingum/app-state-management/error/actions';

import TagSettingsHeader from './components/TagSettingsHeader';
import TagTypeList from './components/TagTypeList';
import CreateTagModal from './components/CreateTagModal';
import MoveOrArchiveChildrenTagsModal from './components/MoveOrArchiveChildrenTagsModal';
import DisplayMessage from '../../widgets/DisplayMessage';

export class TagSettingsComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      openAddTagDialog: false,
      addTagParentTagId: null,
      addTagTagTypeId: null,
      moveOrArchiveTag: null,
      openMoveOrArchiveTagsModal: false,
    };

    if (!props.isFetchingTagTypes) {
      props.getTagTypes(false);
    }

    if (!props.isFetchingTags) {
      props.getTags(false);
    }
  }

  handleAddTagTypeClick = () => {
    const addTagTypeRequest = {
      icon: 'dashboard',
      iconColor: '#9e9e9e',
      name: 'New Tag Type',
      description: '',
    };

    // do not let a new status creation if we still have an original one
    if (this.props.tagTypes.filter((tagType) => tagType.name === addTagTypeRequest.name).length > 0) {
      this.props.addError('You still have a "new" tag type to update. Please provide a new name for the tag type "New Tag Type" before adding more.');
    } else {
      this.props.addTagType(addTagTypeRequest);
    }
  }

  handleAddTagClick = (name) => {
    const addTagRequest = {
      name,
      parentTagId: this.state.addTagParentTagId,
    };
    this.props.addTag(this.state.addTagTagTypeId, addTagRequest);
  }

  handleEditTag = (tagId, request) => {
    this.props.editTag(tagId, request);
  }

  handleUpdateTagList = (tags) => {
    this.props.updateTagList(tags);
  }

  handleOpenAddTagDialog = (tagTypeId, parentTagId) => {
    this.setState({
      openAddTagDialog: true,
      addTagParentTagId: parentTagId,
      addTagTagTypeId: tagTypeId,
    });
  }

  handleCloseAddTagDialog = () => {
    this.setState({
      openAddTagDialog: false,
      addTagParentTagId: null,
    });
  }

  handleCloseMoveOrArchiveTagsModal = () => {
    this.setState({ openMoveOrArchiveTagsModal: false });
  }

  handleArchiveTagClick = (tag) => {
    if (!tag.archived) {
      // archiving
      if (!tag.children || tag.children.length === 0) {
        this.props.editTag(tag.id, { archived: true });
      } else {
        this.setState({
          moveOrArchiveTag: tag,
          openMoveOrArchiveTagsModal: true,
        });
      }
    } else {
      // un-archiving
      this.props.editTag(tag.id, { archived: false });
    }
  }

  handleSuggestionClick = (tagTypeToAdd) => {
    const childrenToAdd = tagTypeToAdd.tags;

    const request = tagTypeToAdd;
    delete request.tags;
    this.props.addTagType(request).then((tagTypeCreated) => {
      this.setState({
        addTagTagTypeId: tagTypeCreated.id,
        addTagParentTagId: null,
      }, () => {
        for (let i = 0; i < childrenToAdd.length; i += 1) {
          this.handleAddTagClick(childrenToAdd[i]);
        }
      });
    });
  }

  render() {
    return (
      <div>
        <TagSettingsHeader
          onShowInformationModal={this.handleInformationModalOpen}
          onAddTagType={this.handleAddTagTypeClick}
          isAddingTagType={this.props.isAddingTagType}
        />
        <TagTypeList
          tagTypes={this.props.tagTypes}
          tags={this.props.tags}
          includeArchived={this.props.includeArchived}
          onEditTag={this.handleEditTag}
          onArchiveTagClick={this.handleArchiveTagClick}
          onTagListUpdate={this.handleUpdateTagList}
          handleOpenAddTagDialog={this.handleOpenAddTagDialog}
        />

        {
          !this.props.isFetchingTagTypes
            && this.props.tagTypes.length === 0
            && (
              <DisplayMessage
                title="No Tags"
                icon="error_outline"
              />
            )
        }

        <CreateTagModal
          isOpen={this.state.openAddTagDialog}
          handleCloseModal={this.handleCloseAddTagDialog}
          onAddTag={this.handleAddTagClick}
        />

        <MoveOrArchiveChildrenTagsModal
          tag={this.state.moveOrArchiveTag}
          tags={this.props.tags}
          handleCloseModal={this.handleCloseMoveOrArchiveTagsModal}
          isOpen={this.state.openMoveOrArchiveTagsModal}
          onTagListUpdate={this.handleUpdateTagList}
        />
      </div>
    );
  }
}

TagSettingsComponent.propTypes = {
  getTagTypes: PropTypes.func.isRequired,
  getTags: PropTypes.func.isRequired,
  addTagType: PropTypes.func.isRequired,
  addTag: PropTypes.func.isRequired,
  updateTagList: PropTypes.func.isRequired,
  editTag: PropTypes.func.isRequired,
  addError: PropTypes.func.isRequired,
  tagTypes: PropTypes.array,
  tags: PropTypes.array,
  isFetchingTagTypes: PropTypes.bool,
  isFetchingTags: PropTypes.bool,
  isAddingTagType: PropTypes.bool,
  includeArchived: PropTypes.bool,
};

TagSettingsComponent.defaultProps = {
  tagTypes: [],
  tags: [],
  isFetchingTagTypes: false,
  isFetchingTags: false,
  isAddingTagType: false,
  includeArchived: false,
};

export const mapStateToProps = (state) => ({
  isFetchingTagTypes: state.tagType.isFetchingTagTypes,
  isFetchingTags: state.tag.isFetchingTag,
  tagTypes: state.tagType.tagTypes,
  tags: state.tag.tagsHierarchy,
  isAddingTagType: state.tagType.isAddingTagType,
  includeArchived: state.tagType.includeArchived,
});

export const mapDispatchToProps = (dispatch) => ({
  getTagTypes: (includeArchived) => dispatch(getTagTypes(includeArchived, false)),
  getTags: (includeArchived) => dispatch(getTags(includeArchived, true)),
  addTagType: (request) => dispatch(addTagType(request)),
  addTag: (tagTypeId, request) => dispatch(addTag(tagTypeId, request)),
  updateTagList: (tags) => dispatch(updateTagList(tags)),
  editTag: (tagId, request) => dispatch(editTag(tagId, request)),
  addError: (error) => dispatch(addError(error)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TagSettingsComponent);
